import { useQuery } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import type { PermissionsResponse, RoleType, PermissionLevel, RolePermission } from '@shared/permissions';
import { hasMinimumPermission, hasResourceAccess, isAdmin } from '@shared/permissions';

async function fetchPermissions(token: string): Promise<PermissionsResponse> {
  const response = await fetch('/api/auth/permissions', {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/json',
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const msg = errorData.error || errorData.details || `Failed to fetch permissions (${response.status})`;
    const err = new Error(msg) as Error & { status: number; code?: string };
    err.status = response.status;
    err.code = errorData.code;
    throw err;
  }

  return response.json();
}

export function usePermissions() {
  const { token, isAuthenticated } = useAuth();

  const query = useQuery<PermissionsResponse>({
    queryKey: ['/api/auth/permissions', token],
    queryFn: () => fetchPermissions(token!),
    enabled: isAuthenticated && !!token,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
  });

  const roles = query.data?.roles ?? {};

  function hasRole(role: RoleType): boolean {
    return role in roles;
  }

  function hasPermission(role: RoleType, minLevel: PermissionLevel): boolean {
    const rolePermission = roles[role];
    if (!rolePermission) return false;
    return hasMinimumPermission(rolePermission.permission, minLevel);
  }

  function getPermissionLevel(role: RoleType): PermissionLevel | null {
    return roles[role]?.permission ?? null;
  }

  function getRoleData(role: RoleType): RolePermission['data'] | null {
    return roles[role]?.data ?? null;
  }

  function getAccessibleResources(
    role: RoleType,
    resourceType: 'business' | 'restaurant' | 'profile'
  ): number[] {
    const rolePermission = roles[role];
    if (!rolePermission) return [];
    if (isAdmin(rolePermission.permission)) return [];
    if (!rolePermission.data) return [];

    switch (resourceType) {
      case 'business': {
        if (rolePermission.data.business_ids) return rolePermission.data.business_ids;
        if (rolePermission.data.business_id != null) return [rolePermission.data.business_id];
        return [];
      }
      case 'restaurant': {
        if (rolePermission.data.restaurant_ids) return rolePermission.data.restaurant_ids;
        if (rolePermission.data.restaurant_id != null) return [rolePermission.data.restaurant_id];
        return [];
      }
      case 'profile': {
        if (rolePermission.data.profiles) return rolePermission.data.profiles;
        if (rolePermission.data.profile != null) return [rolePermission.data.profile];
        return [];
      }
      default:
        return [];
    }
  }

  function canAccessResource(
    role: RoleType,
    resourceType: 'business' | 'restaurant' | 'profile',
    resourceId: number
  ): boolean {
    const rolePermission = roles[role];
    if (!rolePermission) return false;
    return hasResourceAccess(rolePermission, resourceType, resourceId);
  }

  function isPortalUser(): boolean {
    return Object.keys(roles).length > 0;
  }

  function isAdminUser(): boolean {
    return Object.values(roles).some(r => isAdmin(r.permission));
  }

  function hasUnrestrictedAccess(role: RoleType): boolean {
    const rolePermission = roles[role];
    if (!rolePermission) return false;
    return isAdmin(rolePermission.permission);
  }

  return {
    permissions: query.data ?? null,
    roles,
    isLoading: query.isLoading,
    isPending: query.isPending,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,

    hasRole,
    hasPermission,
    getPermissionLevel,
    getRoleData,
    getAccessibleResources,
    canAccessResource,
    isPortalUser,
    isAdminUser,
    hasUnrestrictedAccess,
  };
}
